import React, { useEffect, useRef, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
interface TruncateWithTooltipProps {
  children: React.ReactNode;
  enableTooltip?: boolean;
  placement?: "top" | "bottom" | "left" | "right" | "auto";
  maxWidth?: string | number;
  lines?: number; 
  className?: string;
  style?: React.CSSProperties;
  forceTooltip?: boolean;
}

const TruncateWithTooltip: React.FC<TruncateWithTooltipProps> = ({
  children,
  enableTooltip = true,
  placement = "auto",
  maxWidth = "100%",
  lines = 1,
  className = "",
  style = {},
  forceTooltip = false,
}) => {
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkOverflow = () => {
    const el = textRef.current;
    if (!el) return;

    const isOverflowing =
      el.scrollWidth > el.clientWidth ||
      el.scrollHeight > el.clientHeight;

    setIsTruncated(isOverflowing);
  };

  useEffect(() => {
    checkOverflow();

    const el = textRef.current;
    if (!el) return;

    const resizeObserver = new ResizeObserver(checkOverflow);
    resizeObserver.observe(el);

    window.addEventListener("resize", checkOverflow);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", checkOverflow);
    };
  }, [children, maxWidth, lines]);

  const isMultiLine = lines > 1;

  const truncateStyle: React.CSSProperties = isMultiLine
    ? {
        display: "-webkit-box",
        WebkitLineClamp: lines,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }
    : {
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      };

  const content = (
    <span
      ref={textRef}
      className={`d-inline-block ${className}`}
      style={{
        maxWidth,
        cursor:
          enableTooltip && (isTruncated || forceTooltip)
            ? "pointer"
            : "default",
        ...truncateStyle,
        ...style,
      }}
    >
      {children}
    </span>
  );

  const shouldShowTooltip =
    enableTooltip && (isTruncated || forceTooltip);

  if (!shouldShowTooltip) return content;

  return (
    <OverlayTrigger
      placement={placement}
      delay={{ show: 200, hide: 10 }}
      overlay={
        <Tooltip id={`tooltip-${Math.random()}`}>
          {children}
        </Tooltip>
      }
    >
      {content}
    </OverlayTrigger>
  );
};

export default TruncateWithTooltip;